import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import {
  resetPasswordErrorAtom,
  resetPasswordHashAtom,
} from '@/atoms/resetPassword';
import { useStage } from '@/components/DialogStage';

export const RESET_PASSWORD_HASH_QUERY = 'reset-password-hash';
export const RESET_PASSWORD_ERROR_QUERY = 'reset-password-error';

/**
 * Opens the DialogStage on the reset-password (or forgot-password) scene
 * when the user arrives at /music with the relevant query params after
 * being redirected from the email link's /reset-password/confirm/[hash]
 * route. Strips the query params from the URL once consumed so a refresh
 * doesn't replay the flow.
 *
 * Mounted inside `DialogStage` so it runs as long as the stage is in the
 * tree — no extra plumbing needed at call sites.
 */
const useResetPasswordLinkHandler = () => {
  const router = useRouter();
  const { open } = useStage();
  const setHash = useSetAtom(resetPasswordHashAtom);
  const setError = useSetAtom(resetPasswordErrorAtom);
  const consumedRef = useRef(false);

  useEffect(() => {
    if (!router.isReady || consumedRef.current) {
      return;
    }

    const rawHash = router.query[RESET_PASSWORD_HASH_QUERY];
    const rawError = router.query[RESET_PASSWORD_ERROR_QUERY];
    const hash = Array.isArray(rawHash) ? rawHash[0] : rawHash;
    const error = Array.isArray(rawError) ? rawError[0] : rawError;

    if (!(hash || error)) {
      return;
    }

    consumedRef.current = true;

    if (hash) {
      setHash(hash);
      open('auth.reset-password');
    } else if (error) {
      setError(error);
      open('auth.forgot-password');
    }

    const nextQuery = { ...router.query };
    delete nextQuery[RESET_PASSWORD_HASH_QUERY];
    delete nextQuery[RESET_PASSWORD_ERROR_QUERY];

    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true }
    );
  }, [router, open, setHash, setError]);
};

export default useResetPasswordLinkHandler;
